import { Component, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { BrandRestrictiveUiType } from 'settings/enums/brand-restrictive-ui.enum';
import { ChannelRestrictions } from 'settings/interfaces/brand.interface';
import { BaseModal } from 'shared/abstract/base-modal';
import { TabOutlineComponent } from 'shared/components/tab-outline/tab-outline.component';
import { OutletTypesEnum } from 'shared/enums/outlet-types.enum';
import { CurrentSupplierUserInterface } from 'shared/interfaces/current-supplier-user.interface';
import { FeatureFlagEnum } from 'shared/interfaces/environment.interface';
import { AccessTypeEnum, PermissionAccessZoneEnum } from 'shared/interfaces/permissions.interface';
import { Tab } from 'shared/interfaces/tab.interface';
import { UserApiService } from 'shared/services';
import { FeatureFlagService } from 'shared/services/feature-flag.service';
import { supplierUserHasAccess } from 'shared/util/supplierUserHasAccess';
import { ListingUserPreferencesChannelComparisonComponent } from './listing-user-preferences-channel-comparison/listing-user-preferences-channel-comparison.component';
import { ListingUserPreferencesRestrictiveUiComponent } from './listing-user-preferences-restrictive-ui/listing-user-preferences-restrictive-ui.component';

@Component({
    selector: 'app-listing-user-preferences-modal',
    templateUrl: './listing-user-preferences-modal.component.html',
    styleUrls: ['./listing-user-preferences-modal.component.scss'],
    imports: [TranslateModule, TabOutlineComponent],
})
export class ListingUserPreferencesModalComponent extends BaseModal {
    public selectedTabIndex = 0;
    public data!: {
        listingId: string;
        outletId: string;
        outletTypeId: OutletTypesEnum;
        listingRestrictiveUiType: BrandRestrictiveUiType;
        brandRestrictiveUiChannels: ChannelRestrictions[];
    };

    private loginInfo = toSignal(this.userApiService.getLoginInformationContinuously());
    private featureFlags = this.featureFlagService.featureFlags;

    private hasAccessToChannelSnapshotConfigs = computed(() => {
        const loginInfo = this.loginInfo();
        if (!loginInfo) {
            return false;
        }

        if (!loginInfo.isShiji) {
            const supplierUserDetails = loginInfo as CurrentSupplierUserInterface;
            const hasAccessToSnapshots = supplierUserHasAccess(
                supplierUserDetails.internalPermissions,
                PermissionAccessZoneEnum.ACCESS_TO_CHANNEL_SNAPSHOT_REGENERATION,
                AccessTypeEnum.READ,
            );
            return hasAccessToSnapshots ?? false;
        }

        return true;
    });

    tabs = computed(() => {
        const tabList: Tab[] = [];

        if (this.featureFlags()[FeatureFlagEnum.RESTRICTIVE_UI]) {
            tabList.push({
                label: 'listings.userPreferences.restrictiveUi.title',
                data: {
                    ...this.data,
                    closeModal: (data?: any) => this.closeModalInstance(data),
                },
                component: ListingUserPreferencesRestrictiveUiComponent,
                icon: 'fa fa-eye-slash',
            });
        }

        if (this.featureFlags()[FeatureFlagEnum.LISTING_CHANNEL_COMPARISON] && this.hasAccessToChannelSnapshotConfigs()) {
            tabList.push({
                label: 'listings.userPreferences.channelComparison.title',
                data: {
                    listingId: this.data.listingId,
                    outletId: this.data.outletId,
                    outletTypeId: this.data.outletTypeId,
                    closeModal: (data?: any) => this.closeModalInstance(data),
                },
                component: ListingUserPreferencesChannelComparisonComponent,
                icon: 'fa fa-code-compare',
            });
        }

        return tabList;
    });

    constructor(
        private featureFlagService: FeatureFlagService,
        private userApiService: UserApiService,
    ) {
        super();
    }
}
